import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from '../services/AxiosInstance';
import { getApiUrl } from '../utils/ApiUtils';
import { useSettings } from './SettingsContext';
import { set } from 'date-fns';
// Default features matching the database structure
const defaultFeatures = {
    invoicing: false,
    banking_integration: false,
    expense_management: false,
    payroll: false,
    time_tracking: false,
    multi_currency: false,
    api_access: false,
    document_management: false,
    dashboard_widget: false,
    invoicing_quotes: false,
};
const FeatureContext = createContext({
    features: defaultFeatures,
    isLoading: false,
    hasLoaded: false,
    updateFeature: () => { },
    saveFeatures: () => { }
});
export const FeatureProvider = ({ children }) => {
    const { companyId, registerRefreshFunction } = useSettings();
    const [features, setFeatures] = useState(defaultFeatures);
    const [isLoading, setIsLoading] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);
    const fetchFeatures = async () => {
        if (!companyId) {
            setHasLoaded(true);
            return;
        }
        try {
            setIsLoading(true);
            const url = getApiUrl(`${companyId}/settings/bulk`);
            const { data } = await axios.post(
                url,
                { keys: ['enabled_features'] },
                { withCredentials: true }
            );
            if (data?.settings?.enabled_features) {
                let parsedFeatures;
                try {
                    if (typeof data.settings.enabled_features === 'string') {
                        parsedFeatures = JSON.parse(data.settings.enabled_features);
                    } else {
                        parsedFeatures = data.settings.enabled_features;
                    }
                    setFeatures({
                        ...defaultFeatures,
                        ...parsedFeatures
                    });
                } catch (parseError) {
                    console.error('Error parsing enabled_features JSON:', parseError);
                    setFeatures(defaultFeatures);
                }
            }
        } catch (error) {
            console.error('Error fetching features:', error);
            setFeatures(defaultFeatures);
        } finally {
            setIsLoading(false);
            setHasLoaded(true);
        }
    };
    // Register this context's refresh function with the parent
    useEffect(() => {
        const unregister = registerRefreshFunction('features', fetchFeatures);
        return unregister;
    }, [companyId]);

    // Update a single feature toggle
    const updateFeature = (featureKey, value) => {
        setFeatures(prev => ({
            ...prev,
            [featureKey]: value
        }));
    };
    // Save features to the server
    const saveFeatures = async () => {
        if (!companyId) return;
        try {
            setIsLoading(true);
            const url = getApiUrl(`${companyId}/settings/bulk/update`);
            const updates = [
                { key: 'enabled_features', value: features }
            ];
            const { data } = await axios.post(
                url,
                { updates },
                { withCredentials: true }
            );
            return data.success;
        } catch (error) {
            console.error('Error saving features:', error);
            throw error;
        } finally {
            setIsLoading(false);
        }
    };
    return (
        <FeatureContext.Provider value={{
            features,
            isLoading,
            hasLoaded,
            updateFeature,
            saveFeatures
        }}>
            {children}
        </FeatureContext.Provider>
    );
};
export const useFeatures = () => useContext(FeatureContext);
export default FeatureContext;
